import React, { useState, useEffect } from "react";
import { lookupMember, registerMember, registerEvent, eventInfo } from "../lib/membership";
import { sendChat, getChat } from "../lib/chat";
import { HALLOWEEN } from "../lib/events";
import { Search, IdCard, ArrowLeft, CheckCircle2, Ghost, Ticket, PartyPopper, CalendarDays, UserPlus, Home, MessageSquare, Send, AlertTriangle, ChevronRight, Music, Clock, Ban } from "lucide-react";

const inputCls = "w-full h-16 rounded-2xl bg-[#09090B] border-2 border-white/10 px-5 text-white text-xl font-semibold outline-none focus:border-[#FF007F]";
const labelCls = "block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1";

export default function MemberPortal({ door = false }) {
  const [tab, setTab] = useState(door ? "door" : "home");

  // membership / door lookup
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [member, setMember] = useState(null);
  const [status, setStatus] = useState("out");
  const [visits, setVisits] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // event
  const [eName, setEName] = useState("");
  const [eDob, setEDob] = useState("");
  const [eEmail, setEEmail] = useState("");
  const [eBusy, setEBusy] = useState(false);
  const [eError, setEError] = useState("");
  const [booked, setBooked] = useState(null);
  const [tickets, setTickets] = useState(1);
  const [payMode, setPayMode] = useState("online");
  const [info, setInfo] = useState(null);

  // join
  const [form, setForm] = useState({ name: "", dob: "", email: "", phone: "", parentPhone: "" });
  const [jBusy, setJBusy] = useState(false);
  const [jError, setJError] = useState("");
  const [joined, setJoined] = useState(null);

  // chat
  const [cid, setCid] = useState(localStorage.getItem("yaz_chat_id") || null);
  const [chatName, setChatName] = useState(localStorage.getItem("yaz_chat_name") || "");
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");

  const loadInfo = () => eventInfo(HALLOWEEN.slug).then(setInfo).catch(() => {});
  useEffect(() => { if (!door) loadInfo(); }, [door]);
  useEffect(() => {
    if (tab !== "message" || !cid) return;
    const run = () => getChat(cid).then(setMsgs).catch(() => {});
    run(); const t = setInterval(run, 4000); return () => clearInterval(t);
  }, [tab, cid]);

  const lookup = async () => {
    setError("");
    if (!name.trim() || !dob) { setError("Please enter your name and date of birth."); return; }
    setBusy(true);
    try { const m = await lookupMember(name, dob); setMember(m); setStatus(m.status || "out"); setVisits(m.visits || []); }
    catch (e) { setError(e.response?.status === 404 ? "No membership found. Tap JOIN YAZ to become a member." : "Something went wrong."); }
    finally { setBusy(false); }
  };
  const resetLookup = () => { setMember(null); setName(""); setDob(""); setError(""); setStatus("out"); setVisits([]); };

  const book = async () => {
    setEError("");
    if (!eName.trim() || !eDob.trim() || !eEmail.trim()) { setEError("Please enter your name, phone number and email."); return; }
    setEBusy(true);
    try {
      const res = await registerEvent(HALLOWEEN.slug, { name: eName.trim(), phone: eDob.trim(), email: eEmail.trim() }, tickets, payMode);
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      if (res.full && !res.registered) setEError(res.spots_left ? `Only ${res.spots_left} ticket(s) left — choose fewer.` : "Sorry — the Halloween Rave is SOLD OUT.");
      else setBooked(res);
      loadInfo();
    } catch (e) {
      setEError(e.response?.data?.detail || "Something went wrong.");
    } finally { setEBusy(false); }
  };

  const join = async () => {
    setJError("");
    if (!form.name.trim() || !form.dob || !form.email.trim() || !form.phone.trim()) { setJError("Name, date of birth, email and phone are required."); return; }
    setJBusy(true);
    try {
      setJoined(await registerMember(form));
    } catch (e) { setJError(e.response?.data?.detail || "Something went wrong."); } finally { setJBusy(false); }
  };
  const bookAfterJoin = () => { setEName(joined.name); setEDob(joined.phone || ""); setEEmail(joined.email || ""); setBooked(null); setEError(""); setTab("event"); };

  const sendMsg = async () => {
    const text = draft.trim();
    if (!text) return;
    if (!cid && !chatName.trim()) { return; }
    setDraft("");
    setMsgs((m) => [...m, { id: `tmp-${Date.now()}`, sender: "member", text, at: new Date().toISOString() }]);
    try {
      const res = await sendChat({ conversation_id: cid, name: chatName || "Guest", text });
      if (!cid) { setCid(res.conversation_id); localStorage.setItem("yaz_chat_id", res.conversation_id); localStorage.setItem("yaz_chat_name", chatName || "Guest"); }
      getChat(res.conversation_id).then(setMsgs).catch(() => {});
    } catch (e) {}
  };

  const Brand = (
    <div className="text-center mb-7">
      <h1 className="font-display font-black text-5xl tracking-tight">YAZ<span className="text-[#FF007F]">.</span></h1>
      <p className="font-display font-bold text-sm tracking-[0.3em] text-[#00F0FF] mt-1">BY NEAD</p>
      <p className="text-[#A1A1AA] text-sm mt-2">Youth café · music · games · events in Workington — everyone welcome</p>
    </div>
  );

  const joinForm = (
    <div className="bg-[#18181B] border border-white/10 rounded-3xl p-6">
      <p className="font-display font-black text-2xl mb-1">Become a YAZ member</p>
      <p className="text-[#A1A1AA] mb-5">It's free. Fill this in to get your membership.</p>
      <div className="grid gap-4">
        <label className="block"><span className={labelCls}>Full name *</span><input data-testid="join-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="block"><span className={labelCls}>Date of birth *</span><input data-testid="join-dob" type="date" className={inputCls} value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></label>
        <label className="block"><span className={labelCls}>Email *</span><input data-testid="join-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="block"><span className={labelCls}>Phone *</span><input data-testid="join-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
        <label className="block"><span className={labelCls}>Parent / guardian number (optional)</span><input data-testid="join-parent" className={inputCls} value={form.parentPhone} onChange={(e) => setForm({ ...form, parentPhone: e.target.value })} /></label>
        {jError && <p data-testid="join-error" className="text-[#FF3B30] font-semibold">{jError}</p>}
        <button data-testid="join-submit" onClick={join} disabled={jBusy} className="h-16 rounded-2xl bg-[#FF007F] text-white font-display font-black text-2xl flex items-center justify-center gap-3 active:scale-95 mt-1 disabled:opacity-50"><UserPlus className="w-7 h-7" /> {jBusy ? "…" : "CREATE MY MEMBERSHIP"}</button>
      </div>
    </div>
  );

  if (door) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <img src="/yaz-wall.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 w-full max-w-lg">
          {Brand}
          <div data-testid="door-disabled" className="bg-[#18181B] border border-white/10 rounded-3xl p-8 text-center">
            <AlertTriangle className="w-14 h-14 text-[#FFB800] mx-auto mb-3" />
            <p className="font-display font-black text-3xl">Please sign in at the entrance screen</p>
            <p className="text-[#A1A1AA] mt-3">Phone sign-in is no longer available. Use the touch screen at the YAZ entrance to sign in and out.</p>
          </div>
        </div>
      </div>
    );
  }

  const spots = info ? (info.spots_left ?? null) : null;
  const soldOut = info?.full;
  const TABS = [
    { id: "home", label: "Home", icon: Home },
    { id: "events", label: "Events", icon: Ghost },
    { id: "membership", label: "Membership", icon: IdCard },
    { id: "message", label: "Message", icon: MessageSquare },
  ];
  const activeTab = tab === "event" ? "events" : tab === "join" ? "membership" : tab;

  const Card = ({ testid, onClick, icon: Icon, title, sub, accent }) => (
    <button data-testid={testid} onClick={onClick} className="w-full bg-[#18181B] border border-white/10 rounded-2xl p-5 flex items-center gap-4 text-left hover:border-[#FF007F]/60 active:scale-[0.99] transition-all">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: accent + "22", color: accent }}><Icon className="w-6 h-6" /></div>
      <div className="flex-1"><p className="font-display font-black text-xl text-white leading-tight">{title}</p><p className="text-[#A1A1AA] text-sm">{sub}</p></div>
      <ChevronRight className="w-6 h-6 text-[#52525B]" />
    </button>
  );

  return (
    <div className="min-h-screen bg-[#09090B] text-white relative overflow-hidden">
      <img src="/yaz-wall.webp" alt="" className="fixed inset-0 w-full h-full object-cover" />
      <div className="fixed inset-0 bg-black/80" />
      <div className="relative z-10 max-w-lg mx-auto px-5 pt-10 pb-32">
        {tab === "home" && (
          <div data-testid="signin-warning-banner" className="mb-6 rounded-2xl bg-[#FF3B30] text-white px-5 py-4 font-display font-black text-lg leading-tight flex items-center gap-3 shadow-[0_0_40px_rgba(255,59,48,0.45)]">
            <AlertTriangle className="w-8 h-8 shrink-0" /> SIGN IN AND OUT AT THE ENTRANCE SCREEN EVERY VISIT OR YOUR MEMBERSHIP WILL BE TERMINATED!
          </div>
        )}
        {Brand}

        {tab === "home" && (
          <div data-testid="home-view" className="space-y-3">
            <Card testid="home-rave" onClick={() => setTab("event")} icon={Ghost} accent="#FF007F" title="Book the Halloween Rave" sub={soldOut ? "Sold out" : `${HALLOWEEN.dj} · ${HALLOWEEN.timeLabel} · ${HALLOWEEN.price}`} />
            <Card testid="home-events" onClick={() => setTab("events")} icon={CalendarDays} accent="#00F0FF" title="Upcoming events" sub="See what's on at YAZ" />
            <Card testid="home-membership" onClick={() => setTab("membership")} icon={IdCard} accent="#7C3AED" title="My membership & strikes" sub="View your card and standing" />
            <Card testid="home-join" onClick={() => setTab("join")} icon={UserPlus} accent="#00FA9A" title="Join YAZ" sub="Become a member — it's free" />
            <Card testid="home-message" onClick={() => setTab("message")} icon={MessageSquare} accent="#FF6B00" title="Message YAZ" sub="Chat with the YAZ team" />
          </div>
        )}

        {tab === "events" && (
          <div data-testid="events-view" className="space-y-3">
            <p className="font-display font-black text-3xl mb-2">Upcoming events</p>
            <button data-testid="events-rave" onClick={() => setTab("event")} className="w-full text-left rounded-3xl p-6 bg-gradient-to-br from-[#7C3AED] via-[#FF007F] to-[#FF6B00] active:scale-[0.99] transition-transform">
              <div className="flex items-center justify-between mb-2"><Ghost className="w-9 h-9 text-white" /><span className="px-3 py-1 rounded-full bg-black/40 font-bold text-xs tracking-widest text-[#00FA9A]">{soldOut ? "SOLD OUT" : "TICKETS LIVE"}</span></div>
              <p className="font-display font-black text-3xl">{HALLOWEEN.title}</p>
              <p className="flex items-center gap-2 text-white/90 font-bold mt-2"><CalendarDays className="w-5 h-5" /> {HALLOWEEN.dateLabel} · {HALLOWEEN.timeLabel}</p>
              <p className="text-white/90 font-bold mt-1">{HALLOWEEN.dj} · {HALLOWEEN.price} (first drink free) · No alcohol</p>
              {spots != null && <p className="text-white/85 mt-1">{soldOut ? "No spots left" : `${spots} of ${HALLOWEEN.capacity} spots left`}</p>}
            </button>
          </div>
        )}

        {tab === "event" && (
          <div data-testid="event-view">
            <div className="rounded-3xl p-7 bg-gradient-to-br from-[#7C3AED] via-[#FF007F] to-[#FF6B00] shadow-[0_0_60px_rgba(124,58,237,0.5)] mb-6">
              <div className="flex items-center justify-between mb-4"><Ghost className="w-10 h-10 text-white" /><span className={`px-3 py-1 rounded-full bg-black/40 font-display font-bold text-xs tracking-widest ${soldOut ? "text-[#FF6B60]" : "text-[#00FA9A] animate-pulse"}`}>{soldOut ? "SOLD OUT" : "TICKETS LIVE NOW"}</span></div>
              <p className="font-display font-black text-4xl leading-none">{HALLOWEEN.title}</p>
              <div className="mt-3 space-y-1.5 text-white/90 font-bold">
                <p className="flex items-center gap-2"><CalendarDays className="w-5 h-5" /> {HALLOWEEN.dateLabel}</p>
                <p className="flex items-center gap-2"><Clock className="w-5 h-5" /> {HALLOWEEN.timeLabel}</p>
                <p className="flex items-center gap-2"><Music className="w-5 h-5" /> {HALLOWEEN.dj}</p>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="px-3 py-1.5 rounded-full bg-black/40 text-white font-display font-black">{HALLOWEEN.price} entry</span>
                <span className="px-3 py-1.5 rounded-full bg-[#00FA9A]/20 text-[#00FA9A] font-bold">{HALLOWEEN.perk}</span>
                <span className="px-3 py-1.5 rounded-full bg-black/40 text-white font-bold flex items-center gap-1"><Ban className="w-4 h-4" /> No alcohol</span>
              </div>
              {spots != null && <p data-testid="event-spots" className="font-display font-black text-xl mt-4">{soldOut ? "No spots left" : `${spots} / ${HALLOWEEN.capacity} spots left`}</p>}
            </div>
            {booked ? (
              <div data-testid="event-booked" className="bg-[#18181B] border border-[#00FA9A] rounded-3xl p-7 text-center">
                <PartyPopper className="w-14 h-14 text-[#00FA9A] mx-auto mb-3" />
                <p className="font-display font-black text-3xl">{booked.already ? "You're already on the list!" : "You're booked in!"}</p>
                <p className="text-[#A1A1AA] mt-2">{booked.member_name}</p>
                <p data-testid="event-booked-tickets" className="text-white font-bold mt-2">{booked.tickets || 1} ticket{(booked.tickets || 1) > 1 ? "s" : ""} · {booked.paid ? "Paid online" : `Pay £${(booked.tickets || 1) * HALLOWEEN.priceValue} on arrival`}</p>
                <p className="text-white/80 mt-3">Each ticket includes a free first drink. {HALLOWEEN.dj}, {HALLOWEEN.timeLabel}. No alcohol.</p>
                <button data-testid="event-book-again" onClick={() => { setBooked(null); setEName(""); setEDob(""); setEEmail(""); }} className="mt-5 text-[#71717A] hover:text-white font-bold">Book someone else</button>
              </div>
            ) : (
              <div className="bg-[#18181B] border border-white/10 rounded-3xl p-6">
                <p className="font-display font-black text-2xl mb-1">Book your ticket</p>
                <p className="text-[#A1A1AA] mb-5">Open to everyone — no membership needed.</p>
                <label className="block mb-4"><span className={labelCls}>Full name</span><input data-testid="event-name-input" className={inputCls} value={eName} onChange={(e) => setEName(e.target.value)} /></label>
                <label className="block mb-4"><span className={labelCls}>Phone number</span><input data-testid="event-phone-input" type="tel" inputMode="tel" className={inputCls} value={eDob} onChange={(e) => setEDob(e.target.value)} /></label>
                <label className="block mb-5"><span className={labelCls}>Email</span><input data-testid="event-email-input" type="email" inputMode="email" className={inputCls} value={eEmail} onChange={(e) => setEEmail(e.target.value)} /></label>
                <span className={labelCls}>Tickets</span>
                <div className="grid grid-cols-4 gap-2 mb-5">
                  {Array.from({ length: HALLOWEEN.maxTickets }, (_, i) => i + 1).map((n) => (
                    <button key={n} data-testid={`event-tickets-${n}`} onClick={() => setTickets(n)} className={`h-14 rounded-2xl font-display font-black text-xl border-2 ${tickets === n ? "bg-[#FF007F] border-[#FF007F]" : "bg-[#09090B] border-white/10"}`}>{n}</button>
                  ))}
                </div>
                <span className={labelCls}>How do you want to pay?</span>
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {[["online", "Pay online now"], ["door", "Pay on arrival"]].map(([k, l]) => (
                    <button key={k} data-testid={`event-pay-${k}`} onClick={() => setPayMode(k)} className={`h-14 rounded-2xl font-bold border-2 ${payMode === k ? "bg-[#00F0FF]/15 border-[#00F0FF] text-[#00F0FF]" : "bg-[#09090B] border-white/10"}`}>{l}</button>
                  ))}
                </div>
                <p data-testid="event-total" className="font-display font-black text-xl mb-4">Total: £{tickets * HALLOWEEN.priceValue} <span className="text-sm text-[#A1A1AA] font-bold">{payMode === "online" ? "· secure card payment" : "· pay at the door"}</span></p>
                {eError && <p data-testid="event-error" className="text-[#FF3B30] font-semibold mb-4">{eError}</p>}
                <button data-testid="event-book-button" onClick={book} disabled={eBusy || soldOut} className="w-full h-16 rounded-2xl bg-[#FF007F] text-white font-display font-black text-2xl flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50"><Ticket className="w-7 h-7" /> {soldOut ? "SOLD OUT" : eBusy ? "PLEASE WAIT…" : payMode === "online" ? `PAY £${tickets * HALLOWEEN.priceValue} NOW` : "BOOK — PAY ON ARRIVAL"}</button>
                <button data-testid="event-to-join" onClick={() => setTab("join")} className="w-full mt-3 text-[#00F0FF] font-bold">No membership yet? Join YAZ →</button>
              </div>
            )}
          </div>
        )}

        {tab === "membership" && (
          !member ? (
            <div className="bg-[#18181B] border border-white/10 rounded-3xl p-6 md:p-8">
              <p className="font-display font-black text-3xl mb-1">My membership</p>
              <p className="text-[#A1A1AA] mb-6">Enter your name and date of birth to view your card &amp; strikes</p>
              <label className="block mb-4"><span className={labelCls}>Full name</span><input data-testid="member-name-input" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></label>
              <label className="block mb-5"><span className={labelCls}>Date of birth</span><input data-testid="member-dob-input" type="date" className={inputCls} value={dob} onChange={(e) => setDob(e.target.value)} /></label>
              {error && <p data-testid="member-lookup-error" className="text-[#FF3B30] font-semibold mb-4">{error}</p>}
              <button data-testid="member-lookup-button" onClick={lookup} disabled={busy} className="w-full h-16 rounded-2xl bg-[#FF007F] text-white font-display font-black text-2xl flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50"><Search className="w-7 h-7" /> {busy ? "…" : "VIEW MY MEMBERSHIP"}</button>
              <button onClick={() => setTab("join")} className="w-full mt-3 text-[#00F0FF] font-bold">Not a member yet? Join YAZ →</button>
            </div>
          ) : (
            <div data-testid="member-card">
              <MemberCard member={member} status={status} />
              <div data-testid="member-entrance-note" className="mb-5 rounded-2xl bg-[#00F0FF]/10 border border-[#00F0FF]/40 text-[#00F0FF] px-5 py-4 font-semibold">Sign in and out on the touch screen at the YAZ entrance.</div>
              <VisitsList visits={visits} />
              <button data-testid="member-back-button" onClick={resetLookup} className="mx-auto text-[#71717A] hover:text-white flex items-center gap-2 font-bold"><ArrowLeft className="w-5 h-5" /> Not you? Search again</button>
            </div>
          )
        )}

        {tab === "join" && (
          <div data-testid="join-view">
            {joined ? (
              <div data-testid="join-success" className="bg-[#18181B] border border-[#00FA9A] rounded-3xl p-7 text-center">
                <CheckCircle2 className="w-14 h-14 text-[#00FA9A] mx-auto mb-3" />
                <p className="font-display font-black text-3xl">{joined.already ? "You're already a member!" : "Welcome to YAZ!"}</p>
                <p className="text-[#A1A1AA] mt-2">{joined.name} · <span className="font-mono">{joined.number}</span></p>
                <button data-testid="join-to-event" onClick={bookAfterJoin} className="mt-5 w-full h-16 rounded-2xl bg-[#FF007F] text-white font-display font-black text-xl flex items-center justify-center gap-2 active:scale-95"><Ghost className="w-6 h-6" /> BOOK THE HALLOWEEN RAVE</button>
              </div>
            ) : joinForm}
          </div>
        )}

        {tab === "message" && (
          <div data-testid="message-view" className="bg-[#18181B] border border-white/10 rounded-3xl p-5 flex flex-col h-[70vh]">
            <p className="font-display font-black text-2xl mb-1">Message YAZ</p>
            <p className="text-[#A1A1AA] text-sm mb-3">We'll reply here — keep this screen open.</p>
            {!cid && (
              <input data-testid="chat-name-input" className={inputCls + " mb-3 h-12 text-base"} value={chatName} onChange={(e) => setChatName(e.target.value)} placeholder="Your name" />
            )}
            <div className="flex-1 overflow-y-auto space-y-2 mb-3">
              {msgs.length === 0 && <p className="text-[#71717A] text-sm">Say hi 👋</p>}
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "staff" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[80%] px-4 py-2 rounded-2xl ${m.sender === "staff" ? "bg-[#27272A] text-white" : "bg-[#FF007F] text-white"}`}><p className="whitespace-pre-wrap break-words">{m.text}</p></div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="chat-input" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} className="flex-1 h-14 rounded-xl bg-[#09090B] border border-white/10 px-4 text-white outline-none focus:border-[#FF007F]" placeholder={!cid && !chatName.trim() ? "Enter your name above first" : "Type a message…"} />
              <button data-testid="chat-send" onClick={sendMsg} className="h-14 px-5 rounded-xl bg-[#FF007F] text-white font-bold flex items-center gap-2"><Send className="w-5 h-5" /></button>
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-20 bg-[#18181B]/95 backdrop-blur border-t border-white/10">
        <div className="max-w-lg mx-auto grid grid-cols-4">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`py-3 flex flex-col items-center gap-1 font-display font-bold text-[11px] tracking-wide ${activeTab === t.id ? "text-[#FF007F]" : "text-[#71717A]"}`}>
              <t.icon className="w-6 h-6" /> {t.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MemberCard({ member, status }) {
  const strikes = member.strikes || 0;
  return (
    <div className="rounded-3xl p-7 bg-gradient-to-br from-[#FF007F] to-[#7C3AED] shadow-[0_0_60px_rgba(255,0,127,0.35)] mb-5">
      <div className="flex items-center justify-between mb-6"><IdCard className="w-9 h-9 text-white" /><span className="px-3 py-1 rounded-full bg-black/30 text-white font-display font-bold text-xs tracking-widest">{(member.type || "").toUpperCase()}</span></div>
      <p className="font-display font-black text-4xl leading-none">{member.name}</p>
      <p className="text-white/80 font-mono mt-2 tracking-widest">{member.number}</p>
      <div className="mt-6 flex items-center justify-between">
        <span className={`font-display font-black text-lg flex items-center gap-2 ${status === "in" ? "text-[#00FA9A]" : "text-white/70"}`}><span className={`w-3 h-3 rounded-full ${status === "in" ? "bg-[#00FA9A]" : "bg-white/40"}`} />{status === "in" ? "CURRENTLY IN" : "SIGNED OUT"}</span>
        <span className="text-white/70 text-sm">Since {new Date(member.created_at).toLocaleDateString()}</span>
      </div>
      <div data-testid="member-strikes" className="mt-5 pt-4 border-t border-white/20 flex items-center justify-between">
        <span className="font-display font-bold text-sm tracking-widest text-white/90 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> STRIKES</span>
        <div className="flex items-center gap-2">
          {[0, 1].map((i) => <span key={i} className={`w-5 h-5 rounded-full border-2 border-white/60 ${i < strikes ? "bg-white" : "bg-transparent"}`} />)}
          <span className="font-display font-black ml-1">{strikes}/2</span>
        </div>
      </div>
      {strikes >= 2 && <p className="text-white/90 text-sm mt-2 font-semibold">Please speak to a staff member.</p>}
    </div>
  );
}

function VisitsList({ visits }) {
  return (
    <div className="bg-[#18181B] border border-white/10 rounded-2xl p-4 mb-5">
      <p className="font-display font-bold text-sm tracking-widest text-[#A1A1AA] mb-2">RECENT VISITS</p>
      {visits.length === 0 ? <p className="text-[#71717A] text-sm">No visits yet.</p> : visits.map((v) => (
        <div key={v.id} className="flex justify-between py-1.5 border-b border-white/5 last:border-0 text-sm">
          <span className={v.type === "in" ? "text-[#00FA9A] font-bold" : "text-[#FF007F] font-bold"}>{v.type === "in" ? "Signed in" : "Signed out"}</span>
          <span className="text-[#A1A1AA]">{new Date(v.at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}
